import { useState } from 'react';
import { useMarket, MARKETS } from '@/contexts/MarketContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCart } from '@/contexts/CartContext';

export function MarketSelector() {
  const { currentMarket, setMarket } = useMarket();
  const { items } = useCart();
  const [pendingMarket, setPendingMarket] = useState<string | null>(null);
  
  const handleMarketChange = (marketSlug: string) => {
    if (marketSlug === currentMarket.slug) return;
    
    // If cart has items, show confirmation dialog
    if (items.length > 0) {
      setPendingMarket(marketSlug);
    } else {
      setMarket(marketSlug, false);
    }
  };

  const confirmMarketChange = () => {
    if (pendingMarket) {
      setMarket(pendingMarket, true); // Clear cart
      setPendingMarket(null);
    }
  };

  const cancelMarketChange = () => {
    setPendingMarket(null);
  };

  return (
    <>
      <Select value={currentMarket.slug} onValueChange={handleMarketChange}>
        <SelectTrigger className="w-8 h-8 p-0 border-0 bg-transparent hover:bg-accent/30 transition-colors rounded-full flex items-center justify-center [&>svg]:hidden">
          <img 
            src={currentMarket.flag} 
            alt={currentMarket.name}
            className="w-8 h-8 rounded-full object-cover"
          />
        </SelectTrigger>
        <SelectContent className="bg-card border-border shadow-lg z-[100] min-w-[220px]">
          {MARKETS.map((market) => (
            <SelectItem 
              key={market.slug} 
              value={market.slug}
              className="cursor-pointer hover:bg-accent focus:bg-accent py-2.5"
            >
              <span className="flex items-center gap-3 w-full">
                <img 
                  src={market.flag} 
                  alt={market.name}
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                />
                <span className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="font-semibold text-base">{market.currencySymbol}</span>
                  <span className="text-sm font-medium">{market.currency}</span>
                </span>
                <span className="text-xs text-muted-foreground truncate">{market.name}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <AlertDialog open={!!pendingMarket} onOpenChange={() => setPendingMarket(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Market?</AlertDialogTitle>
            <AlertDialogDescription>
              Changing your market will clear your cart because prices may differ between regions. 
              You currently have {items.length} item{items.length !== 1 ? 's' : ''} in your cart.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelMarketChange}>Keep Current Market</AlertDialogCancel>
            <AlertDialogAction onClick={confirmMarketChange}>
              Change Market & Clear Cart
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
